import { createElement, FC, useEffect, useRef, useState } from "react";
import { Column } from "@tanstack/table-core";
import classNames from "classnames";
import { Badge } from "../../../../../Badge";
import { Button } from "../../../../../Button";
import { Dropdown } from "../../../../../Dropdown";
import { renderColumnHeader } from "../../../../utils";

import { ReactComponent as Chevron } from "./icons/chevron-down.svg";
import { ReactComponent as CloseIcon } from "./icons/close.svg";

import styles from "./styles.module.scss";

export type FilterProps = {
  column: Column<Object, any>
}

export const Filter: FC<FilterProps> = (({ column }) => {
  const buttonRef = useRef<HTMLElement>();
  const [ isOpen, setIsOpen ] = useState(false);
  const [ tempValue, setTempValue ] = useState<unknown>(null);

  const filterValue = column.getFilterValue();
  const columnMeta = column.columnDef.meta!;
  const { value, badgeValue } = columnMeta.prettifyFilterValue!(filterValue, columnMeta.defaultFilterFn as any);

  useEffect(() => {
    if (isOpen) {
      setTempValue(filterValue);
    }
  }, [ filterValue, isOpen ]);

  const isPlaceholder = filterValue === undefined;

  return (
    <>
      <Button
        buttonSize="md"
        buttonType="secondaryGray"
        className={classNames({ [styles.placeholderButton]: isPlaceholder })}
        onClick={() => setIsOpen(true)}
        ref={buttonRef}
      >
        <div className={styles.text}>
          {renderColumnHeader(column)}
          {!isPlaceholder && <span>: {value}</span>}
        </div>
        {
          isPlaceholder
            ? <Chevron />
            : <>
              {
                badgeValue &&
                <Badge
                  className={styles.badge}
                  badgeType="badgeColor"
                  badgeSize="sm"
                  badgeColor="gray"
                >
                  {badgeValue}
                </Badge>
              }
              <div
                className={styles.removeButton}
                role="button"
                onClick={event => {
                  event.stopPropagation();
                  column.setFilterValue(undefined);
                }}
              >
                <CloseIcon />
              </div>
            </>
        }
      </Button>
      <Dropdown
        floatingOptions={{ placement: "bottom-start" }}
        isOpen={isOpen}
        referenceElement={buttonRef.current}
        closeOnClick="overlay"
        onRequestClose={() => setIsOpen(false)}
        footer={
          <div className={styles.footer}>
            <Button
              buttonSize="md"
              buttonType="tertiaryColor"
              onClick={() => {
                column.setFilterValue(undefined);
                setIsOpen(false);
              }}
            >
              Clear
            </Button>
            <Button
              buttonSize="md"
              buttonType="primary"
              onClick={() => {
                column?.setFilterValue(tempValue);
                setIsOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        }
      >
        <div className={styles.filterBlock}>
          {
            createElement(
              column.columnDef.meta!.Filter!,
              {
                column: column,
                label: <>Filter by {renderColumnHeader(column)}</>,
                filterFn: column.columnDef.meta!.defaultFilterFn as any,
                value: tempValue,
                onChange: setTempValue,
                autoFocus: true,
              },
            )
          }
        </div>
      </Dropdown>
    </>
  );
});